import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { Link } from 'react-router-dom'
import Button from '../components/ui/Button'
import FormError from '../components/ui/FormError'
import BookingStatusBadge from '../components/customer/BookingStatusBadge'
import {
  CANCEL_BOOKING_MUTATION,
  MY_BOOKINGS_QUERY,
  type CancelBookingData,
  type CancelBookingVariables,
  type CustomerBooking,
  type MyBookingsData,
  type MyBookingsVariables,
} from '../graphql/customer'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import { localized } from '../lib/i18n'

const translations = {
  el: {
    eyebrow: 'Κρατήσεις',
    title: 'Όλες οι κρατήσεις σου σε ένα timeline',
    body: 'Φιλτράρισε τα ραντεβού σου, άνοιξε λεπτομέρειες και ακύρωσε όσα ακόμη επιτρέπουν ενέργειες.',
    loading: 'Φορτώνουμε τις κρατήσεις σου...',
    empty: 'Δεν βρέθηκαν κρατήσεις για αυτό το φίλτρο.',
    loadMore: 'Φόρτωσε περισσότερες',
    provider: 'Επαγγελματίας',
    schedule: 'Πρόγραμμα',
    address: 'Διεύθυνση',
    details: 'Λεπτομέρειες',
    cancel: 'Ακύρωση',
    total: 'σύνολο',
    all: 'Όλες',
    upcoming: 'Επερχόμενες',
    pending: 'Σε αναμονή',
    completed: 'Ολοκληρωμένες',
    cancelled: 'Ακυρωμένες',
    today: 'Σήμερα',
    tomorrow: 'Αύριο',
  },
  en: {
    eyebrow: 'Bookings',
    title: 'All your bookings in one timeline',
    body: 'Filter appointments, open details, and cancel the ones that still allow customer actions.',
    loading: 'Loading your bookings...',
    empty: 'No bookings matched this filter.',
    loadMore: 'Load more',
    provider: 'Provider',
    schedule: 'Schedule',
    address: 'Address',
    details: 'Details',
    cancel: 'Cancel',
    total: 'total',
    all: 'All',
    upcoming: 'Upcoming',
    pending: 'Pending',
    completed: 'Completed',
    cancelled: 'Cancelled',
    today: 'Today',
    tomorrow: 'Tomorrow',
  },
} as const

type FilterKey = 'all' | 'upcoming' | 'pending' | 'completed' | 'cancelled'

const PAGE_SIZE = 10

function normalizeTime(timeValue: string) {
  return timeValue.slice(0, 5)
}

function formatDate(dateValue: string, locale: 'el' | 'en', copy: { today: string; tomorrow: string }) {
  const today = new Date()
  const date = new Date(`${dateValue}T12:00:00`)
  const tomorrow = new Date()
  tomorrow.setDate(today.getDate() + 1)

  if (dateValue === today.toISOString().slice(0, 10)) return copy.today
  if (dateValue === tomorrow.toISOString().slice(0, 10)) return copy.tomorrow

  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

function providerName(booking: CustomerBooking) {
  return `${booking.providerProfile.user.firstName} ${booking.providerProfile.user.lastName}`
}

function bookingAddress(booking: CustomerBooking) {
  if (!booking.property) return null

  return [booking.property.label, booking.property.addressLine1, booking.property.city, booking.property.postalCode]
    .filter(Boolean)
    .join(', ')
}

function formatCurrency(cents: number, locale: 'el' | 'en') {
  return new Intl.NumberFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

const secondaryLinkClass = 'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2'

export default function BookingsListPage() {
  const { locale, t } = useLang()
  const copy = useScopedTranslations('bookings.list', translations[locale])
  const [filter, setFilter] = useState<FilterKey>('all')
  const [errors, setErrors] = useState<string[]>([])
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null)

  const variables = useMemo<MyBookingsVariables>(() => {
    if (filter === 'upcoming') return { upcoming: true, first: PAGE_SIZE }
    if (filter === 'pending') return { status: 'pending', first: PAGE_SIZE }
    if (filter === 'completed') return { status: 'completed', first: PAGE_SIZE }
    if (filter === 'cancelled') return { status: 'cancelled', first: PAGE_SIZE }
    return { first: PAGE_SIZE }
  }, [filter])

  const { data, loading, fetchMore, refetch } = useQuery<MyBookingsData, MyBookingsVariables>(MY_BOOKINGS_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
  })
  const [cancelBooking] = useMutation<CancelBookingData, CancelBookingVariables>(CANCEL_BOOKING_MUTATION)

  async function handleCancel(bookingId: string) {
    if (!window.confirm(t('Να ακυρωθεί αυτή η κράτηση;', 'Cancel this booking?'))) return

    setErrors([])
    setActiveBookingId(bookingId)

    try {
      const { data: mutationData } = await cancelBooking({ variables: { bookingId } })
      const mutationErrors = mutationData?.cancelBooking.errors ?? [t('Δεν ήταν δυνατή η ακύρωση.', 'Could not cancel the booking.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        return
      }

      await refetch()
    } catch {
      setErrors([t('Δεν ήταν δυνατή η ακύρωση.', 'Could not cancel the booking.')])
    } finally {
      setActiveBookingId(null)
    }
  }

  const filters: Array<{ key: FilterKey; label: string }> = [
    { key: 'all', label: copy.all },
    { key: 'upcoming', label: copy.upcoming },
    { key: 'pending', label: copy.pending },
    { key: 'completed', label: copy.completed },
    { key: 'cancelled', label: copy.cancelled },
  ]

  const bookings = data?.myBookings.nodes ?? []
  const pageInfo = data?.myBookings.pageInfo

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">{copy.eyebrow}</p>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">{copy.title}</h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">{copy.body}</p>
        </div>
        <p className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600">
          {data?.myBookings.totalCount ?? 0} {copy.total}
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        {filters.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setFilter(item.key)}
            className={[
              'rounded-full border px-4 py-2 text-sm font-semibold transition',
              item.key === filter
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700',
            ].join(' ')}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <FormError errors={errors} />
      </div>

      {loading && bookings.length === 0 ? (
        <p className="mt-8 text-sm text-slate-500">{copy.loading}</p>
      ) : bookings.length === 0 ? (
        <div className="mt-8 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
          {copy.empty}
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {bookings.map((booking) => (
            <article key={booking.id} className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <BookingStatusBadge status={booking.status} />
                  <h2 className="mt-3 text-xl font-bold text-slate-950">{localized(booking.serviceType, locale)}</h2>
                  <p className="mt-2 text-sm text-slate-600">
                    {copy.provider}: {providerName(booking)}
                  </p>
                </div>
                <p className="text-lg font-bold text-slate-950">{formatCurrency(booking.priceCents, locale)}</p>
              </div>

              <dl className="mt-5 grid gap-4 text-sm text-slate-600 md:grid-cols-3">
                <div>
                  <dt className="font-semibold text-slate-950">{copy.schedule}</dt>
                  <dd className="mt-1">{formatDate(booking.scheduledDate, locale, copy)} · {normalizeTime(booking.startTime)} - {normalizeTime(booking.endTime)}</dd>
                </div>
                {bookingAddress(booking) && (
                  <div className="md:col-span-2">
                    <dt className="font-semibold text-slate-950">{copy.address}</dt>
                    <dd className="mt-1">{bookingAddress(booking)}</dd>
                  </div>
                )}
              </dl>

              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <Link to={`/bookings/${booking.id}`} className={secondaryLinkClass}>
                  {copy.details}
                </Link>
                {['pending', 'confirmed'].includes(booking.status) && (
                  <Button
                    type="button"
                    variant="ghost"
                    loading={activeBookingId === booking.id}
                    onClick={() => handleCancel(booking.id)}
                  >
                    {copy.cancel}
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {pageInfo?.hasNextPage && (
        <div className="mt-8 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            onClick={() => fetchMore({
              variables: { ...variables, after: pageInfo.endCursor },
              updateQuery: (previous, { fetchMoreResult }) => {
                if (!fetchMoreResult) return previous

                return {
                  myBookings: {
                    ...fetchMoreResult.myBookings,
                    nodes: [...previous.myBookings.nodes, ...fetchMoreResult.myBookings.nodes],
                  },
                }
              },
            })}
          >
            {copy.loadMore}
          </Button>
        </div>
      )}
    </div>
  )
}
